// src/pages/teacher/exams/TeacherExams.js
import React, { useEffect, useMemo, useState, useDeferredValue } from "react";
import {
  Card, Row, Col, Button, Badge, Form, InputGroup, Spinner, Alert, Modal, Dropdown
} from "react-bootstrap";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import {
  collection, query, where, onSnapshot, limit,
  getDoc, getDocs, doc, setDoc, deleteDoc, serverTimestamp, updateDoc, writeBatch
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";

dayjs.extend(relativeTime);

/* ---------------- helpers ---------------- */
const lower = (s) => String(s || "").trim().toLowerCase();
const toU = (s) => String(s || "").trim().toUpperCase();
const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map((x) => x.trim()).filter(Boolean);
  // split by comma/pipe/slash (not by spaces to keep "Class 12 PCM" intact)
  return String(raw).split(/[,\|/]+/g).map((x) => x.trim()).filter(Boolean);
};
const toEndTime = (start = "10:00", dur = 60) => {
  const [h, m] = String(start || "00:00").split(":").map((x) => parseInt(x || 0, 10));
  const total = h * 60 + m + (parseInt(dur || 0, 10) || 0);
  const hh = Math.floor((total % (24 * 60)) / 60).toString().padStart(2, "0");
  const mm = (total % 60).toString().padStart(2, "0");
  return `${hh}:${mm}`;
};

// Idempotency: tiny FNV-1a 32-bit hash for deterministic schedule ids
function hash32(str = "") {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/* Merge class/section mapping (teacher + profile). Supports:
   - classIds / classes / classNames / className
   - sectionsByClass map, or flat sections/section applied to all classes
*/
function resolveScope(raw = {}, profile = {}) {
  const uniq = (arr) => Array.from(new Set(arr));

  const classesRaw = [
    ...parseList(raw.classIds || raw.classes || raw.classNames || raw.className),
    ...parseList(profile.classIds || profile.classes || profile.classNames || profile.className),
  ];
  const classIds = uniq(classesRaw).slice(0, 40);

  const flatSections = uniq([
    ...parseList(raw.sections || raw.section).map(toU),
    ...parseList(profile.sections || profile.section).map(toU),
  ]);

  const sbcRaw = typeof raw.sectionsByClass === "object" && raw.sectionsByClass ? raw.sectionsByClass : {};
  const sbcProf = typeof profile.sectionsByClass === "object" && profile.sectionsByClass ? profile.sectionsByClass : {};

  // Build merged sectionsByClass
  const sectionsByClass = {};
  classIds.forEach((c) => {
    const a = parseList(sbcRaw[c]).map(toU);
    const b = parseList(sbcProf[c]).map(toU);
    const merged = a.length || b.length ? uniq([...a, ...b]) : flatSections;
    sectionsByClass[c] = merged;
  });

  const sectionIds = uniq(
    Object.values(sectionsByClass).flat().length ? Object.values(sectionsByClass).flat() : flatSections
  );

  return { classIds, sectionIds, sectionsByClass };
}

/* ──────────────────────────────────────────────── */
/* Lightweight skeleton card for loading state     */
/* ──────────────────────────────────────────────── */
function TemplateSkeleton() {
  return (
    <div className="template-card skeleton">
      <div className="skeleton-bar w-60" />
      <div className="skeleton-bar w-40 mt-2" />
      <div className="skeleton-pills mt-3">
        <span className="skeleton-pill" />
        <span className="skeleton-pill" />
        <span className="skeleton-pill" />
      </div>
      <div className="d-flex gap-2 mt-3">
        <div className="skeleton-btn" />
        <div className="skeleton-btn" />
      </div>
    </div>
  );
}

/* =================================================================== */

export default function TeacherExams() {
  const nav = useNavigate();
  const { user, profile } = useAuth();
  const { schoolId, loading: schoolLoading } = useSchool();

  // teacher record (for mapping)
  const [teacher, setTeacher] = useState(null);
  const [tLoading, setTLoading] = useState(true);

  // templates visible to teacher
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  // schedule modal
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [mode, setMode] = useState("live");          // 'live' | 'mock'
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [start, setStart] = useState("10:00");
  const [dur, setDur] = useState(60);
  const end = useMemo(() => toEndTime(start, dur), [start, dur]);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState({});

  // publish controls
  const [isPublic, setIsPublic] = useState(false);
  const [autoPublish, setAutoPublish] = useState(true);

  const isTeacherInactive = (teacher, profile) => {
    const s = String(teacher?.status ?? profile?.status ?? "").trim().toLowerCase();
    const hasActiveFlag =
      typeof teacher?.active === "boolean" ? !teacher.active
      : typeof profile?.active === "boolean" ? !profile.active
      : null;
    return s === "inactive" || s === "disabled" || s === "blocked" || hasActiveFlag === true;
  };

  /* --------- load teacher doc (robust) --------- */
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!schoolId || !user?.uid) { setTeacher(null); setTLoading(false); return; }
      setTLoading(true);
      try {
        if (profile?.teacherId) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", profile.teacherId));
          if (!cancelled && snap.exists()) { setTeacher({ id: snap.id, ...(snap.data()||{}) }); setTLoading(false); return; }
        }
        const email = lower(user?.email || "");
        if (email) {
          const qs = await getDocs(
            query(collection(db, "schools", schoolId, "teachers"), where("email", "==", email), limit(1))
          );
          if (!cancelled && !qs.empty) {
            const d = qs.docs[0]; setTeacher({ id: d.id, ...(d.data()||{}) }); setTLoading(false); return;
          }
        }
        setTeacher(null);
      } catch { setTeacher(null); }
      if (!cancelled) setTLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, [schoolId, user?.uid, user?.email, profile?.teacherId]);

  const scope = useMemo(() => resolveScope(teacher || {}, profile || {}), [teacher, profile]);
  const inactive = isTeacherInactive(teacher, profile);

  /* --------- filters: pick class & section from mapping --------- */
  const [fClass, setFClass] = useState("");
  const sectionsAvail = useMemo(() => (fClass ? (scope.sectionsByClass?.[fClass] || []) : []), [scope, fClass]);
  const [fSection, setFSection] = useState(""); // "" = All Sections

  useEffect(() => {
    const first = scope.classIds?.[0] || "";
    setFClass(first);
    setFSection("");
  }, [JSON.stringify(scope.classIds)]); // reinit when mapping changes

  useEffect(() => {
    // reset section if it doesn't exist in the new class
    if (fSection && sectionsAvail.length && !sectionsAvail.includes(fSection)) {
      setFSection("");
    }
  }, [fClass, JSON.stringify(sectionsAvail)]);

  /* --------- subscribe to templates for teacher's classes --------- */
  useEffect(() => {
    if (!schoolId) return;
    setTplLoading(true);

    const unsubs = [];
    const got = new Map();

    const publish = () => {
      const rows = Array.from(got.values()).sort((a, b) => {
        const ta = a.updatedAt?.toDate?.() ? a.updatedAt.toDate() : 0;
        const tb = b.updatedAt?.toDate?.() ? b.updatedAt.toDate() : 0;
        return tb - ta;
      });
      setTemplates(rows);
      setTplLoading(false);
    };

    const classes = scope.classIds || [];
    if (classes.length === 0) {
      setTemplates([]);
      setTplLoading(false);
      return () => unsubs.forEach((u) => u && u());
    }

    // Firestore limitation: array-contains-any max 10 → chunk
    const chunks = [];
    for (let i = 0; i < classes.length; i += 10) chunks.push(classes.slice(i, i + 10));

    chunks.forEach((vals) => {
      const ref = collection(db, "schools", schoolId, "examTemplates");
      const qy = query(ref, where("classIds", "array-contains-any", vals));
      const u = onSnapshot(
        qy,
        (snap) => {
          snap.docs.forEach((d) => got.set(d.id, { id: d.id, ...(d.data() || {}) }));
          publish();
        },
        () => setTplLoading(false)
      );
      unsubs.push(u);
    });

    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, JSON.stringify(scope.classIds)]);

  /* --------- template list filtering (search + class + section) --------- */
  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();

    const sectionMatch = (t) => {
      if (!fClass) return true;
      if (!fSection) return (t.classIds || []).includes(fClass);

      // If template stores per-class sections, honor it; else treat as "all sections"
      const map = t.sectionKeysByClass || t.sectionsByClass || {};
      const list = Array.isArray(map?.[fClass]) ? map[fClass].map(toU) : [];
      if (!list.length) return (t.classIds || []).includes(fClass); // no restriction in template
      return (t.classIds || []).includes(fClass) && list.includes(toU(fSection));
    };

    return templates
      .filter((t) => {
        if (q) {
          const hay = [
            t.name, t.examType, t.testType, t.testTypeLabel,
            ...(t.classIds || []),
          ].join(" ").toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return sectionMatch(t);
      });
  }, [templates, deferredSearch, fClass, fSection]);

  /* --------- permissions & CRUD helpers --------- */
  const role = String(profile?.role || "").toLowerCase();
  const isAdminish = ["admin", "schooladmin", "owner", "superadmin"].includes(role);
  const isCreator = (t) => t?.createdBy === user?.uid || t?.createdByUid === user?.uid;
  const canDelete = (t) => isAdminish || isCreator(t);
  const canEdit = (t) => isAdminish || isCreator(t);

  const setTemplateStatus = async (row, nextStatus) => {
    if (!schoolId || !row?.id) return;
    try {
      setStatusBusy((m) => ({ ...m, [row.id]: true }));
      await updateDoc(
        doc(db, "schools", schoolId, "examTemplates", row.id),
        { status: nextStatus, updatedAt: serverTimestamp() }
      );
    } catch (e) {
      alert(e?.message || "Failed to update status.");
    } finally {
      setStatusBusy((m) => ({ ...m, [row.id]: false }));
    }
  };

  const editTemplate = (t) => {
    nav(`/teacher/exams/templates/${t.id}`, {
      state: {
        templateSeed: t,
        allowedClasses: scope.classIds,
        sectionsByClass: scope.sectionsByClass,
      },
    });
  };

  const duplicateTemplate = async (t) => {
    if (!schoolId || !t) return;
    try {
      const ref = doc(collection(db, "schools", schoolId, "examTemplates"));
      const { id, createdAt, updatedAt, status, visibility, ...rest } = t;
      const copy = {
        ...rest,
        name: `${t.name || "Template"} (Copy)`,
        status: "draft",
        visibility: "draft",
        createdBy: user?.uid || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await setDoc(ref, copy, { merge: true });
      alert("Template duplicated as Draft.");
    } catch (e) {
      alert(e?.message || "Failed to duplicate template.");
    }
  };

  const deleteTemplate = async (t) => {
    if (!schoolId || !t?.id) return;
    if (!canDelete(t)) { alert("You don’t have permission to delete this template."); return; }
    const ok = window.confirm(`Delete template “${t.name || "Untitled"}”? This cannot be undone.`);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "schools", schoolId, "examTemplates", t.id));
      alert("Template deleted.");
    } catch (e) {
      alert(e?.message || "Failed to delete template.");
    }
  };

  /* --------- schedule exam --------- */
  const openSchedule = (row) => {
    setTpl(row);
    setMode("live");
    setDate(dayjs().format("YYYY-MM-DD"));
    setStart("10:00");
    setDur(60);
    setOpen(true);
    setIsPublic(false);
    setAutoPublish(true);
  };
  const closeSchedule = () => { setOpen(false); setTpl(null); };

  const schedule = async () => {
    if (!schoolId || !tpl) return;
    if (mode === "live" && (!date || !start || !dur)) return;

    // Use the teacher’s *full* mapping (multi-class + per-class sections)
    const classIds = (scope.classIds || []).slice(0, 10);
    if (!classIds.length) return;

    const upperSectionsByClass = {};
    classIds.forEach((c) => {
      const arr = (scope.sectionsByClass?.[c] || []).map(toU);
      upperSectionsByClass[c] = arr;
    });

    const targets = classIds.flatMap((cid) => {
      const secs = upperSectionsByClass[cid];
      const eff = secs.length ? secs : [""];
      return eff.map((s) => ({
        classId: cid,
        className: cid,      // keeping in sync with your earlier fields
        sectionKey: s,       // uppercase or "" (all)
        sectionLabel: s || "",
        branchKey: "",
        branchLabel: "",
      }));
    });

    const base = {
      name: tpl.name || "Exam",
      templateId: tpl.id,
      examType: tpl.examType || "objective",
      testType: tpl.testType || "daily_test",
      testTypeLabel: tpl.testTypeLabel || "",
      examMode: mode, // 'live' | 'mock'
      classIds,
      sectionKeysByClass: upperSectionsByClass,
      branchKeysByClass: {},

      targets,
      status: "scheduled",
      visibility: isPublic ? "public" : "scheduled",
      isPublished: false,
      autoPublishAtStart: mode === "live" ? autoPublish : false,
      publishAt: null,

      date: mode === "live" ? date : null,
      startTime: mode === "live" ? start : null,
      endTime: mode === "live" ? toEndTime(start, dur) : null,
      durationMins: mode === "live" ? Number(dur) || 0 : null,

      isPublic,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: user?.uid || "",
      createdByRole: "teacher",
    };

    // deterministic schedule id
    const keyStr = [
      tpl.id,
      mode,
      mode === "live" ? `${date}|${start}|${dur}` : "mock",
      classIds.slice().sort().join(","),
      Object.entries(upperSectionsByClass)
        .sort(([a],[b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}:${v.slice().sort().join(".")}`).join("|"),
    ].join("|");
    const docId = `sched_${hash32(keyStr)}_${(user?.uid || "nouid").slice(-6)}`;
    const examRef = doc(db, "schools", schoolId, "exams", docId);

    try {
      setSaving(true);

      const existing = await getDoc(examRef);
      if (!existing.exists()) {
        await setDoc(examRef, base, { merge: false });
      } else {
        const { createdAt, ...patch } = base;
        await updateDoc(examRef, { ...patch, updatedAt: serverTimestamp() });
      }

      // === COPY QUESTIONS ===
      const tplQCol = collection(db, "schools", schoolId, "examTemplates", tpl.id, "questions");
      const tplQsSnap = await getDocs(tplQCol);

      if (!tplQsSnap.empty) {
        const normalize = (q) => {
          const options = q.options ?? q.choices ?? q.opts ?? [];
          const type = (q.type || q.qType || "mcq").toLowerCase();
          return {
            order: q.order ?? q.index ?? 0,
            section: q.section || "",
            subject: q.subject || "",
            prompt: q.prompt || q.question || "",
            type,
            options: Array.isArray(options)
              ? options.map((o, i) => ({
                  key: o.key ?? o.value ?? String.fromCharCode(65 + i),
                  label: o.label ?? o.text ?? o.title ?? "",
                  correct: !!(o.correct ?? o.isCorrect ?? false),
                }))
              : [],
            points: Number(q.points ?? q.marks ?? 1),
            difficulty: q.difficulty || "",
            explanation: q.explanation || "",
            ...["imageUrl","code","tags","negativeMarks"].reduce((acc, k) => (q[k] !== undefined ? (acc[k]=q[k], acc) : acc), {}),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
        };

        let batch = writeBatch(db);
        let count = 0;
        for (const d of tplQsSnap.docs) {
          const qData = normalize(d.data() || {});
          const qRef = doc(collection(db, "schools", schoolId, "exams", docId, "questions"));
          batch.set(qRef, qData);
          count++;
          if (count % 450 === 0) {
            await batch.commit();
            batch = writeBatch(db);
          }
        }
        await batch.commit();
      }

      setSaving(false);
      setOpen(false);
      alert("Exam scheduled. Questions copied to the exam.");
    } catch (e) {
      setSaving(false);
      alert(e?.message || "Failed to schedule.");
    }
  };

  const showMappingHint = !tplLoading && (!scope.classIds || scope.classIds.length === 0);

  /* --------- UI --------- */
  if (schoolLoading || tLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
    );
  }

  if (!tLoading && teacher && inactive) {
    return (
      <div className="container-fluid p-0">
        <Card className="border-0 shadow-sm mt-3 mx-2">
          <Card.Body className="py-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="bi bi-lock-fill text-danger fs-5" />
              <div className="fw-semibold">Access restricted</div>
            </div>
            <div className="text-muted">
              Your teacher account is marked as <b>Inactive</b>. Please contact the administrator to regain access.
            </div>
            <div className="mt-3 d-flex gap-2">
              <Button variant="secondary" onClick={() => window.location.assign("/")}>Go Home</Button>
              <Button variant="outline-secondary" onClick={() => window.location.assign("/support")}>Contact Support</Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      {/* Hero / Header */}
      <div className="te-hero mb-3">
        <div className="te-hero-inner">
          <div>
            <div className="tdb-eyebrow">Teacher • Exams</div>
            <h5 className="m-0 fw-bold">Templates & Scheduling</h5>
            <div className="text-muted small">See templates for your classes, manage questions, schedule, and use quick actions.</div>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="success"
              onClick={() =>
                nav('/teacher/exams/templates/create', {
                  state: {
                    allowedClasses: scope.classIds,
                    sectionsByClass: scope.sectionsByClass,
                  },
                })
              }
            >
              <i className="bi bi-plus-lg me-1" /> Create Template
            </Button>
            <Button variant="outline-secondary" onClick={() => window.location.reload()}>
              <i className="bi bi-arrow-repeat me-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Mapping hint (only after load) */}
      {showMappingHint && (
        <Alert variant="warning" className="mb-3">
          Your class/section mapping isn’t set. You won’t see any templates.
          Ask Admin to map you to Classes/Sections.
        </Alert>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label className="mb-1">Search templates</Form.Label>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, type, test type…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <Button variant="outline-secondary" onClick={() => setSearch("")} aria-label="Clear search">
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label className="mb-1">Class</Form.Label>
              <Form.Select value={fClass} onChange={(e)=>setFClass(e.target.value)}>
                {scope.classIds?.length ? scope.classIds.map((c)=>(
                  <option key={c} value={c}>{c}</option>
                )) : <option value="">—</option>}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="mb-1">Section</Form.Label>
              <Form.Select value={fSection} onChange={(e)=>setFSection(toU(e.target.value))} disabled={!fClass}>
                <option value="">All Sections</option>
                {sectionsAvail.map((s)=> <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md="auto" className="ms-auto">
              <Badge bg="light" text="dark" className="border">
                {tplLoading ? "…" : `${filtered.length} template${filtered.length === 1 ? "" : "s"}`}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Templates board */}
      <Card className="templates-board border-0">
        <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
          <div className="fw-semibold">Templates</div>
          <Badge bg="light" text="dark" className="border">{tplLoading ? "…" : filtered.length}</Badge>
        </Card.Header>
        <Card.Body className="p-2">
          {tplLoading ? (
            <div className="templates-grid">
              {Array.from({ length: 6 }).map((_, i) => <TemplateSkeleton key={i} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-muted text-center">No templates for the selected class/section.</div>
          ) : (
            <div className="templates-grid">
              {filtered.map((t) => {
                const ready = String(t.status || "draft").toLowerCase() === "ready";
                const canDel = canDelete(t);
                const canEdt = canEdit(t);
                const tmplSections = (t.sectionKeysByClass || t.sectionsByClass || {})[fClass] || [];
                return (
                  <div key={t.id} className={`template-card ${ready ? "" : "is-draft"}`}>
                    <div className="card-particles" aria-hidden />
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="me-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="fw-semibold text-truncate" title={t.name || "-"}>{t.name || "-"}</span>
                          <Badge bg={String(t.examType || "objective").toLowerCase() === "descriptive" ? "warning" : "info"}>
                            {t.examType || "objective"}
                          </Badge>
                          {t.testTypeLabel ? <Badge bg="light" text="dark">{t.testTypeLabel}</Badge> : null}
                          <Badge bg={ready ? "success" : "secondary"}>{ready ? "Ready" : "Draft"}</Badge>
                          <Button
                            size="sm"
                            variant={ready ? "outline-warning" : "success"}
                            disabled={!!statusBusy[t.id]}
                            onClick={() => setTemplateStatus(t, ready ? "draft" : "ready")}
                          >
                            {statusBusy[t.id] ? "Saving…" : (ready ? "Mark Draft" : "Mark Ready")}
                          </Button>
                        </div>
                        <div className="text-muted small mt-1">
                          {(t.totalQuestions || 0)} questions · Updated{" "}
                          {t.updatedAt?.toDate ? dayjs(t.updatedAt.toDate()).fromNow() : "—"}
                        </div>
                      </div>

                      {/* Actions menu */}
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="outline-secondary" size="sm" title="More actions">
                          <i className="bi bi-three-dots-vertical" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => editTemplate(t)}>
                            <i className="bi bi-pencil me-2" /> Edit template
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => duplicateTemplate(t)}>
                            <i className="bi bi-files me-2" /> Duplicate
                          </Dropdown.Item>
                          {canDel && <Dropdown.Divider />}
                          {canDel && (
                            <Dropdown.Item className="text-danger" onClick={() => deleteTemplate(t)}>
                              <i className="bi bi-trash me-2" /> Delete
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {(t.classIds || []).slice(0, 6).map((c) => (
                        <Badge key={c} bg="light" text="dark" className="border">{c}</Badge>
                      ))}
                      {(t.classIds || []).length > 6 && (
                        <Badge bg="light" text="dark" className="border">+{(t.classIds || []).length - 6}</Badge>
                      )}
                      {/* Show sections if defined for the selected class */}
                      {Array.isArray(tmplSections) && tmplSections.slice(0, 6).map((s) => (
                        <Badge key={`${t.id}-${s}`} bg="secondary">{s}</Badge>
                      ))}
                    </div>

                    <div className="mt-auto pt-3 d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => nav(`/teacher/exams/templates/${t.id}/view`)}
                        title="View questions grouped by subject & section"
                      >
                        <i className="bi bi-eye me-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => nav(`/teacher/exams/templates/${t.id}/questions`)}
                        title="Add or manage questions"
                        aria-label="Manage questions"
                      >
                        <i className="bi bi-list-check me-1" /> Questions
                      </Button>

                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => openSchedule(t)}
                        disabled={!ready || (t.totalQuestions || 0) === 0}
                        title={!ready ? "Template must be Ready" : ((t.totalQuestions || 0) === 0 ? "Add questions first" : "Schedule")}
                        aria-disabled={!ready || (t.totalQuestions || 0) === 0}
                      >
                        <i className="bi bi-calendar2-week me-1" /> Schedule
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Schedule modal (Live / Mock) */}
      <Modal show={open} onHide={closeSchedule} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule · {tpl?.name || ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!tpl ? (
            <div className="p-3 text-center text-muted">Select a template.</div>
          ) : (
            <>
              <Form.Label className="fw-semibold mb-1">Mode</Form.Label>
              <div className="d-flex gap-4 mb-3">
                <Form.Check
                  type="radio"
                  id="m-live"
                  name="exam-mode"
                  label="Live (fixed date & time)"
                  checked={mode === "live"}
                  onChange={() => setMode("live")}
                />
                <Form.Check
                  type="radio"
                  id="m-mock"
                  name="exam-mode"
                  label="Mock / Practice (anytime)"
                  checked={mode === "mock"}
                  onChange={() => setMode("mock")}
                />
              </div>

              {mode === "live" && (
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Start Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={start}
                        onChange={(e) => setStart(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Duration (mins)</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={dur}
                        onChange={(e) => setDur(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>End Time</Form.Label>
                      <Form.Control type="time" value={end} readOnly />
                      <Form.Text muted>Auto-calculated</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* Publish options */}
              <hr className="my-3" />
              <Form.Label className="fw-semibold mb-2">Publish</Form.Label>
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  type="switch"
                  id="pub-public"
                  label="Public (students can discover in practice/when published)"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="pub-auto"
                  label="Auto-publish at start time"
                  checked={autoPublish}
                  disabled={mode !== "live"}
                  onChange={(e) => setAutoPublish(e.target.checked)}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSchedule}>Close</Button>
          <Button variant="primary" onClick={schedule} disabled={!tpl || saving}>
            {saving ? "Saving…" : "Schedule"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Local styles */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .template-card,
          .template-card:hover,
          .card-particles {
            animation: none !important;
            transition: none !important;
            transform: none !important;
          }
        }
        .tdb-eyebrow { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--bs-secondary-color); }

        .te-hero {
          border: 1px solid var(--border);
          border-radius: 16px;
          background:
            radial-gradient(60% 120% at 10% -20%, rgba(var(--brand-accent-rgb, 106,140,255), .16) 0%, transparent 60%),
            radial-gradient(40% 120% at 110% 10%, rgba(var(--brand-accent-rgb, 106,140,255), .10) 0%, transparent 60%),
            var(--surface, #fff);
        }
        @supports (background: color-mix(in oklab, white 50%, black)) {
          .te-hero {
            background:
              radial-gradient(60% 120% at 10% -20%, color-mix(in oklab, var(--brand-accent) 16%, transparent) 0%, transparent 60%),
              radial-gradient(40% 120% at 110% 10%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%),
              color-mix(in oklab, var(--surface) 96%, transparent);
          }
        }
        .te-hero-inner { display:flex; align-items:center; justify-content:space-between; padding: 14px 16px; border-radius: 16px; }

        .templates-board { border-radius: 16px; }
        .templates-board .card-header {
          position: sticky; top: 0; z-index: 2;
          backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          border-bottom: 1px solid var(--border);
        }

        .templates-grid{
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 14px;
        }
        @media (max-width: 576px){
          .templates-grid{ grid-template-columns: 1fr; }
        }

        .template-card {
          position: relative;
          display: flex;
          flex-direction: column;
          min-height: 168px;
          padding: 14px;
          border-radius: 16px;
          border: 1px solid var(--border);
          background:
            linear-gradient(180deg, rgba(var(--brand-accent-rgb, 106,140,255), .06) 0%, transparent 60%),
            color-mix(in oklab, var(--surface) 96%, transparent);
          box-shadow: 0 .6rem 1.35rem rgba(0,0,0,.08);
          transition: transform .18s ease, box-shadow .18s ease, background .2s ease, border-color .2s ease;
        }
        [data-bs-theme="dark"] .template-card { box-shadow: 0 .6rem 1.35rem rgba(0,0,0,.35); }
        .template-card:hover { transform: translateY(-2px); box-shadow: 0 .8rem 1.6rem rgba(0,0,0,.12); }
        .template-card.is-draft { opacity: .96; }

        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(color-mix(in oklab, var(--brand-primary) 18%, transparent) 1px, transparent 1px),
            radial-gradient(color-mix(in oklab, var(--brand-accent) 18%, transparent) 1px, transparent 1px);
          background-position: 0 0, 12px 12px; background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite; opacity: .18;
          border-radius: 16px;
        }
        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
        }

        .skeleton { overflow: hidden; }
        .skeleton-bar{
          height: 14px; border-radius: 8px;
          background: linear-gradient(90deg, rgba(0,0,0,.08) 25%, rgba(0,0,0,.12) 37%, rgba(0,0,0,.08) 63%);
          background-size: 400% 100%;
          animation: shimmer 1.2s ease-in-out infinite;
        }
        [data-bs-theme="dark"] .skeleton-bar{
          background: linear-gradient(90deg, rgba(255,255,255,.08) 25%, rgba(255,255,255,.12) 37%, rgba(255,255,255,.08) 63%);
        }
        .w-60{ width: 60%; } .w-40{ width: 40%; }
        .skeleton-pills{ display:flex; gap:8px; }
        .skeleton-pill{ width:80px; height:22px; border-radius: 999px; background-color: rgba(0,0,0,.08); }
        [data-bs-theme="dark"] .skeleton-pill{ background-color: rgba(255,255,255,.08); }
        .skeleton-btn{ width:110px; height:32px; border-radius:8px; background-color: rgba(0,0,0,.08); }
        [data-bs-theme="dark"] .skeleton-btn{ background-color: rgba(255,255,255,.08); }
        @keyframes shimmer { 0%{background-position: 100% 0} 100%{background-position: 0 0} }

        .btn-accent {
          --accent: var(--brand-accent);
          background-color: var(--accent) !important;
          border-color: var(--accent) !important;
          color: #fff !important;
          box-shadow: 0 .25rem .6rem rgba(var(--brand-accent-rgb, 106,140,255), .35);
        }
        .btn-accent:hover, .btn-accent:focus { filter: brightness(0.92); color: #fff !important; }
      `}</style>
    </div>
  );
}
